import {DragOverlay} from '@dnd-kit/core';
import {arrayMove} from '@dnd-kit/sortable';
import {DropdownItem, openDropdown, Upload} from 'platform/components';
import {Box, foundationTheme, Grid, Icon, Show} from 'platform/foundation';
import {Lightbox, useLightbox} from 'platform/lightbox';

import {useState} from 'react';

import {isNil, isNotNil} from 'ramda';

import {buildSortableItemId, DnDContext, parseDndItemId, SortableWrapper} from '@dms/features/dnd';
import {SaleVehiclePhoto} from '@dms/features/vehicle-forms-kit';
import i18n from '@dms/i18n';
import {useDmsLightboxActionsFactory} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {useSaleVehicleSimpleAlbum} from '../hooks/useSaleVehicleSimpleAlbum';
import {useUploadPreview} from '../hooks/useUploadPreview';
import {SimpleAlbumImage} from './SimpleAlbumImage';

const ALLOWED_MIME_TYPES_TO_UPLOAD = ['image/jpeg', 'image/png', 'image/jpg'].join(', ');

interface SimpleAlbumProps extends RequiredTestIdProps {
  images: SaleVehiclePhoto[];
  onImagesChange: (
    images: SaleVehiclePhoto[] | ((prevImages: SaleVehiclePhoto[]) => SaleVehiclePhoto[])
  ) => void;
}

const IMAGE_SIZE = {
  width: 58,
  height: 43,
} as const;

const CONTAINER_ID = 'sale-vehicle-simple-album';

export const SimpleAlbum = (props: SimpleAlbumProps) => {
  const [lightboxControls, {onOpen}] = useLightbox(CONTAINER_ID);
  const lightboxActionsFactory = useDmsLightboxActionsFactory();

  const [activeDnDUrl, setActiveDnDUrl] = useState<string | null>(null);

  const {previews, ...uploadPreviews} = useUploadPreview();

  const {uploadFileToAlbum, isUploading} = useSaleVehicleSimpleAlbum({
    onUploadStart: (file) => {
      uploadPreviews.createPreview(file);
    },
    onQueueProcessed: (uploadedFiles) => {
      uploadPreviews.clearPreviews();
      props.onImagesChange((prevImages) => [...prevImages, ...uploadedFiles]);
    },
  });

  const deleteImage = (imageId: string) => {
    props.onImagesChange((prevImages) =>
      prevImages.filter((prevImage) => prevImage.fileId !== imageId)
    );
  };

  return (
    <>
      <DnDContext
        onItemDragStart={(item) => setActiveDnDUrl(item.url ?? null)}
        onDragEnd={({active, over}) => {
          const {position: draggedItemIndex} = parseDndItemId(active.id);
          if (isNil(over)) {
            return;
          }
          const {position: droppedOntoItemIndex} = parseDndItemId(over.id);

          if (isNotNil(draggedItemIndex) && isNotNil(droppedOntoItemIndex)) {
            props.onImagesChange(
              (prevImages) =>
                arrayMove(prevImages, draggedItemIndex, droppedOntoItemIndex) ?? prevImages
            );
          }
        }}
      >
        <Grid
          fill
          minColumnWidth={IMAGE_SIZE.width}
          minHeight={IMAGE_SIZE.height}
          height="100%"
          spacing={3}
        >
          <SortableWrapper
            disabled={isUploading}
            containerId={CONTAINER_ID}
            items={props.images.map((image) => ({
              id: image.fileId,
              url: image.fileUri,
            }))}
          >
            {props.images.map((image, index) => (
              <SimpleAlbumImage
                key={image.fileId}
                isDisabled={isUploading}
                id={buildSortableItemId(image.fileId, CONTAINER_ID, image.fileUri, index)}
                data-testid={suffixTestId('simpleAlbumImage', props)}
                src={prepareAlbumImageUrl(image.fileUri)}
                onClick={() => onOpen(index)}
                onContextMenu={(event) => {
                  event.preventDefault();
                  openDropdown(
                    event,
                    <>
                      <DropdownItem
                        label={i18n.t('general.actions.delete')}
                        prefix={<Icon value="action/delete" />}
                        isDisabled={isUploading}
                        onClick={() => {
                          deleteImage(image.fileId);
                        }}
                      />
                    </>
                  );
                }}
              />
            ))}
            {previews.map((preview) => (
              <SimpleAlbumImage
                key={preview.id}
                id={preview.id}
                src={preview.uri}
                isGhost
                isLoading
                data-testid={suffixTestId('simpleAlbumImagePreview', props)}
              />
            ))}
            <Box width="100%" height="100%" ratio="4 / 3">
              <Upload
                isDisabled={isUploading}
                data-testid={suffixTestId('simpleAlbum', props)}
                type="card"
                size="default"
                accept={ALLOWED_MIME_TYPES_TO_UPLOAD}
                isMultiple
                shouldValidateAccept
                uploadIcon="image/photo_camera"
                customRequest={({file, onSuccess, onError}) => {
                  if (file instanceof File) {
                    uploadFileToAlbum(file, {
                      onSuccess,
                      onError,
                    });
                  }
                }}
              />
            </Box>
          </SortableWrapper>
        </Grid>
        <DragOverlay>
          <Show when={isNotNil(activeDnDUrl)}>
            <Box width={IMAGE_SIZE.width} height={IMAGE_SIZE.height}>
              <SimpleAlbumImage
                id="sortable:dragOverlay"
                src={prepareAlbumImageUrl(activeDnDUrl!)}
                data-testid={suffixTestId('simpleAlbumImagePreview', props)}
              />
            </Box>
          </Show>
        </DragOverlay>
      </DnDContext>
      <Lightbox
        data-testid={suffixTestId('lightbox', props)}
        controls={lightboxControls}
        images={lightboxActionsFactory.addFullActions(
          props.images.map((image) => ({
            id: image.fileId,
            url: image.fileUri,
            resizeUrl: image.fileUri,
          })),
          (image) => [
            {
              id: 'deleteBtn',
              icon: 'action/delete',
              onClick: () => deleteImage(image.id),
            },
          ]
        )}
      />
      {lightboxActionsFactory.photoDownloadSizeDialog}
    </>
  );
};

const prepareAlbumImageUrl = (url: string) => {
  const queryDelimiter = url?.includes('?') ? '&' : '?';
  // width query param is expecting pixels
  return `${url}${queryDelimiter}width=${IMAGE_SIZE.width * (foundationTheme.space.step * 16)}`;
};
